/**
 * The mode registry. `docs/prompts/03-solo-modes.md`'s gate: "Adding a
 * seventh mode requires creating one file and registering it, with zero
 * changes to `packages/engine` and zero changes to the renderer" — still
 * true here, mirrors `packages/themes/src/registry.ts`'s shape for the same
 * reason (BASELINE 4, adding a theme).
 *
 * `docs/decisions/0021` changes what "one file" produces: a descriptor is no
 * longer a fixed value per mode id, it is built from a `ModeConfig`
 * (`config.ts`) — so the registry holds *factories*, `MODE_ID -> (config) =>
 * ModeDescriptor`, and `buildMode` is the one place that turns a config into
 * the concrete descriptor `startMode` then runs. Every caller downstream of
 * `buildMode` — `startMode`, the HUD, the game loop — still only ever sees a
 * plain `ModeDescriptor`, unaware that a config built it.
 */
import { createGame } from '@interlock/engine';
import type { GameState } from '@interlock/engine';

import type { ModeConfig } from './config.js';
import { buildSprintMode } from './sprint.js';
import { buildSurvivalMode } from './survival.js';
import type { ModeDescriptor, ModeId, ModeRuntime } from './types.js';

/** Registry order: `docs/DESIGN.md`'s "Solo" order, sprint then survival. */
export const MODE_IDS: readonly ModeId[] = ['sprint', 'survival'];

type ModeFactory = (config: ModeConfig) => ModeDescriptor;

const MODE_FACTORIES: Readonly<Record<ModeId, ModeFactory>> = {
  sprint: buildSprintMode,
  survival: buildSurvivalMode,
};

/** True when `id` names a real mode. Useful before trusting a value read from a URL or storage. */
export function isModeId(id: string): id is ModeId {
  return Object.hasOwn(MODE_FACTORIES, id);
}

/**
 * Build a mode's descriptor for a given configuration. Throws for an id
 * outside `MODE_IDS` — a defensive fail-loud, not an expected path: every
 * caller in this codebase already has a config whose `modeId` came from
 * `MODE_IDS` or `isModeId`.
 */
export function buildMode(config: ModeConfig): ModeDescriptor {
  const factory = MODE_FACTORIES[config.modeId];
  if (factory === undefined) throw new Error(`unknown mode id: ${config.modeId}`);
  return factory(config);
}

/** A fresh `GameState` for `mode`, applying its declared engine flags. */
export function createModeGame(mode: ModeDescriptor, seed: number): GameState {
  return createGame({ seed, ...mode.engineOptions });
}

/**
 * Start a run: create the game, create the runtime, run `setup`. This is the
 * one place that ordering lives, so a caller (the client's game screen, a
 * test) cannot create the two out of step with each other.
 */
export function startMode(
  mode: ModeDescriptor,
  seed: number,
): { state: GameState; runtime: ModeRuntime } {
  const state = createModeGame(mode, seed);
  const runtime: ModeRuntime = { elapsedMs: 0 };
  mode.setup?.(state, runtime);
  return { state, runtime };
}
